import { Routes } from '@angular/router';
import { AppLayoutComponent } from './core/layout/shell/app-layout/app-layout.component';
import {
  adminToolsGuard,
  authGuard,
  loginGuard,
  pageContentGuard,
  permissionGuard,
  rootRedirectGuard,
} from './core/guards/auth.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { SYSTEM_SECTIONS } from './core/services/auth.service';

export const routes: Routes = [
  {
    path: 'login',
    title: 'Login',
    loadComponent: () => import('./features/auth/login/login').then((m) => m.Login),
    canActivate: [loginGuard],
  },
  {
    path: 'forgot-password',
    title: 'Forgot password',
    loadComponent: () =>
      import('./features/auth/forgot-password/forgot-password').then((m) => m.ForgotPassword),
  },
  {
    path: 'reset-password/:code',
    title: 'Reset password',
    loadComponent: () =>
      import('./features/auth/reset-password/reset-password').then((m) => m.ResetPassword),
  },
  {
    path: 'cards/external/:key',
    title: 'PT card request',
    loadComponent: () =>
      import('./features/pt-cards/external/pt-card-external-form').then(
        (m) => m.PtCardExternalForm,
      ),
  },
  {
    path: '',
    pathMatch: 'full',
    component: AppLayoutComponent,
    canActivate: [rootRedirectGuard],
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [authGuard],
    canActivateChild: [pageContentGuard],
    children: [
      {
        path: 'audits',
        title: 'Audit list',
        loadChildren: () => import('./features/audits/audits.routes').then((m) => m.AUDITS_ROUTES),
      },
      {
        path: 'audit',
        title: 'Audits',
        loadChildren: () => import('./features/audits/audits.routes').then((m) => m.AUDIT_ROUTES),
      },
      {
        path: 'group',
        title: 'Customers',
        loadChildren: () =>
          import('./features/customers/manage/customer.routes').then(
            (m) => m.CUSTOMER_MANAGE_ROUTES,
          ),
      },
      {
        path: 'users',
        title: 'User list',
        loadChildren: () => import('./features/users/users.routes').then((m) => m.USERS_ROUTES),
      },
      {
        path: 'dashboard',
        title: 'Executive dashboard',
        canActivate: [permissionGuard],
        data: { sectionId: SYSTEM_SECTIONS.dashboard, permissionAction: 'read' },
        loadComponent: () =>
          import('./features/executive-dashboard/executive-dashboard').then(
            (m) => m.ExecutiveDashboard,
          ),
      },
      {
        path: 'email-mailbox',
        title: 'Email Inbox',
        canActivate: [permissionGuard],
        data: { sectionId: SYSTEM_SECTIONS.emailInbox, permissionAction: 'read' },
        loadComponent: () =>
          import('./features/email-mailbox/email-mailbox').then((m) => m.EmailMailbox),
      },
      {
        path: 'approvals',
        title: 'Approval list',
        loadChildren: () =>
          import('./features/approvals/approvals.routes').then((m) => m.APPROVALS_ROUTES),
      },
      {
        path: 'withdrawn-certificates',
        title: 'Withdrawn / Relinquished Certificates',
        canActivate: [permissionGuard],
        data: { sectionId: SYSTEM_SECTIONS.withdrawnCertificates, permissionAction: 'read' },
        loadComponent: () =>
          import('./features/approvals/listing/withdrawn/withdrawn-certificates-listing').then(
            (m) => m.WithdrawnCertificatesListing,
          ),
      },
      {
        path: 'cards',
        title: 'PT card list',
        loadChildren: () =>
          import('./features/pt-cards/pt-card.routes').then((m) => m.PT_CARD_ROUTES),
      },
      {
        path: 'recommendations',
        title: 'Recommendations',
        canActivate: [permissionGuard],
        data: { sectionId: SYSTEM_SECTIONS.recommendations, permissionAction: 'read' },
        loadComponent: () =>
          import('./features/recommendations/listing/recommendations/recommendations-listing').then(
            (m) => m.RecommendationsListing,
          ),
      },
      {
        path: 'ltqls',
        title: 'LTQL list',
        loadChildren: () => import('./features/ltqls/ltqls.routes').then((m) => m.LTQLS_ROUTES),
      },
      {
        path: 'LTQLs',
        title: 'LTQL list',
        loadChildren: () => import('./features/ltqls/ltqls.routes').then((m) => m.LTQLS_ROUTES),
      },
      {
        path: 'country',
        title: 'Travel advice',
        canActivate: [permissionGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { sectionId: SYSTEM_SECTIONS.countryManagement, permissionAction: 'read' },
        loadComponent: () =>
          import('./features/travel-advice/listing/travel-advice-listing').then(
            (m) => m.TravelAdviceListing,
          ),
      },
      {
        path: 'complaints',
        title: 'Complaint list',
        loadChildren: () =>
          import('./features/complaints/complaints.routes').then((m) => m.COMPLAINTS_ROUTES),
      },
      {
        path: 'raisedinvoices',
        title: 'Raised invoices',
        canActivate: [permissionGuard],
        data: { sectionId: SYSTEM_SECTIONS.raisedInvoices, permissionAction: 'read' },
        loadComponent: () =>
          import('./features/invoicing/raised/listing/raised-invoices').then(
            (m) => m.RaisedInvoices,
          ),
      },
      {
        path: 'noncompliance',
        title: 'Non-compliance list',
        loadChildren: () =>
          import('./features/non-compliances/non-compliance.routes').then(
            (m) => m.NON_COMPLIANCE_ROUTES,
          ),
      },
      {
        path: 'reporting/:reportSlug',
        title: 'Reporting',
        canActivate: [permissionGuard],
        data: { sectionId: SYSTEM_SECTIONS.reporting, permissionAction: 'read' },
        loadComponent: () => import('./features/reporting/reporting').then((m) => m.Reporting),
      },
      {
        path: 'reporting',
        title: 'Reporting',
        canActivate: [permissionGuard],
        data: { sectionId: SYSTEM_SECTIONS.reporting, permissionAction: 'read' },
        loadComponent: () => import('./features/reporting/reporting').then((m) => m.Reporting),
      },
      {
        path: 'suppliers',
        title: 'Supplier list',
        loadChildren: () =>
          import('./features/suppliers/suppliers.routes').then((m) => m.SUPPLIERS_ROUTES),
      },
      {
        path: 'invoicing',
        title: 'Invoicing',
        loadChildren: () =>
          import('./features/invoicing/invoicing.routes').then((m) => m.INVOICING_ROUTES),
      },
      {
        path: 'application',
        title: 'Application list',
        loadChildren: () =>
          import('./features/applications/application.routes').then((m) => m.APPLICATION_ROUTES),
      },
      {
        path: 'maintenance',
        title: 'Maintenance',
        loadChildren: () =>
          import('./features/maintenance/maintenance.routes').then((m) => m.MAINTENANCE_ROUTES),
      },
      {
        path: 'admin-tools',
        canActivate: [adminToolsGuard],
        canActivateChild: [adminToolsGuard],
        loadChildren: () =>
          import('./features/admin-tools/admin-tools.routes').then((m) => m.ADMIN_TOOLS_ROUTES),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
